import Hero from '../components/sections/Hero';
import { Wrench, Settings, FileText, ClipboardCheck } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Carousel from '../components/ui/Carousel';
import servicesImage from '../assets/images/Atelier/tablee_globale1.jpg';
import styles from '../styles/pages/Services.module.css';

// Importation dynamique des images
import { importServiceImages } from '../utils/imageImporter';

// Importation des images par service
const diagnosticImages = importServiceImages('diagnostic');
const expertiseImages = importServiceImages('expertise');
const reparationImages = importServiceImages('reparation');
const revisionImages = importServiceImages('revision');
const maintenanceImages = importServiceImages('maintenance');
const contratsImages = importServiceImages('contrats');

const services = [
  {
    id: 'diagnostic',
    title: 'Diagnostic technique',
    description: (
      <>
      <p>Objectifs : Enquête technique sur les symptômes et identifier rapidement les problèmes.</p>
      <br/>
      <ul>
        <li>Analyse approfondie des pannes et des dysfonctionnements électriques et mécaniques sur tous types de moteurs diesel industriels et navals.</li>
        <li><strong>Valise de diagnostic</strong> : lecture, effacement de codes défauts, analyse, paramétrage.</li>
        <li>Analyse des codes erreur (ECU) : lecture et interprétation des défauts.</li>
      </ul>
      </>
    ),
    icon: <ClipboardCheck size={48} className={styles.serviceIcon} />,
    images: diagnosticImages
  },
  {
    id: 'expertise',
    title: 'Expertise',
    description: (
      <>
      <ul>
        <li>Expertise technique : état des composants, mesure des performances et contrôles fonctionnels.</li>
        <li>Rapport de diagnostic avec recommandations personnalisées et plan d'action adapté à vos besoins.</li>
        <li>Mesures des performances : puissance, consommation, etc.</li>
        <li>Contrôles endoscopiques, mesure des compressions, etc.</li>
        <li>Tests de compression, contrôle endoscopique, inspection visuelle interne.</li>
        <li>Rapport d'expertise détaillé sur l'état de santé moteur.</li>
      </ul>
      </>
    ),
    icon: <Wrench size={48} className={styles.serviceIcon} />,
    images: expertiseImages
  },
  {
    id: 'reparation',
    title: 'Réparation',
    description: (
      <>
      <p>Objectifs : restaurer les performances d'origine et garantir la fiabilité sur le long terme.</p>
      <br/>
      <ul>
        <li>Réparation de moteurs diesel industriels toutes marques, toutes puissances, toutes applications.</li>
        <li>Intervention sur site ou en atelier.</li>
        <li>Remplacement des pièces défectueuses par des pièces d'origine ou équivalentes.</li>
        <li><strong>Réglages et Calibrations : </strong>calibrage injecteurs, vanne EGR, boîtier papillon.</li>
        <li>Réglages et tests de performance après réparation.</li>
      </ul>
      </>
    ),
    icon: <Wrench size={48} className={styles.serviceIcon} />,
    images: reparationImages
  },
  {
    id: 'revision',
    title: 'Révision complète & Reconditionnement',
    description: (
      <>
      <p>Objectif : Offrir les mêmes garanties fonctionnelles que l'origine.</p>
      <br/>
      <ul>
        <li>Expertise de chaque composant avec contrôle dimensionnel.</li>
        <li>Remplacement ou remise en état des composants suivant préconisation constructeur.</li>
        <li>Reconditionnement selon les normes constructeur.</li>
        <li>Rapport technique détaillé.</li>
      </ul>
      <p>Avantages : conserver le moteur d'origine, réduire les coûts et les délais.</p>
      </>
    ),
    icon: <Wrench size={48} className={styles.serviceIcon} />,
    images: revisionImages
  },
  {
    id: 'maintenance',
    title: 'Maintenance industrielle',
    description: (
      <>
      <ul>
        <li>Véhicules-ateliers d'intervention équipés de valises de diagnostic, outillages spécialisés et pièces de rechange courantes.</li>
        <li>Réactivité et efficacité pour réduire au maximum les temps d'immobilisation.</li>
        <li>Usinage et réparation des pièces défectueuses.</li>
        <li>Métrologie et contrôle qualité.</li>
        <li>Études mécaniques adaptées.</li>
        <li>Calculs mécaniques.</li>
        <li>DAO (Dessin Assisté par Ordinateur).</li>
      </ul>
      </>
    ),
    icon: <FileText size={48} className={styles.serviceIcon} />,
    images: maintenanceImages
  },
  {
    id: 'contrats-entretien',
    title: 'Contrats d\'entretien',
    description: (
      <>
      <p>Objectifs : prolonger la durée de vie du matériel et optimiser la disponibilité des équipements.</p>
      <br/>
      <ul>
        <li>Contrats personnalisés selon la nature de vos équipements et votre rythme d'utilisation.</li>
        <li>Suivi planifié : visites régulières, contrôles, vidanges et remplacements préventifs.</li>
        <li>Rapports techniques détaillés après chaque intervention.</li>
        <li>Assistance prioritaire en cas de panne.</li>
      </ul>
      </>
    ),
    icon: <FileText size={48} className={styles.serviceIcon} />,
    images: contratsImages
  },
];

const Services = () => {
    const location = useLocation();
    const serviceRefs = useRef({});

    useEffect(() => {
    if (location.hash) {
        setTimeout(() => {
            const element = document.getElementById(location.hash.substring(1));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 300);
    }
}, [location]);

    return (
        <div className={styles.container}>
            <Hero
                title="Services"
                subtitle="Découvrez l'ensemble des prestations que nous proposons."
                backgroundImage={servicesImage}
            />

            <div className={styles.servicesContainer}>
                {services.map((service) => (
                    <div 
                        key={service.id} 
                        id={service.id}
                        ref={el => serviceRefs.current[service.id] = el}
                        className={styles.serviceRow}
                    >
                        <div className={styles.serviceContent}>
                            {service.icon}
                            <h3 className={styles.serviceTitle}>
                                {service.title}
                            </h3>
                            <p className={styles.serviceDesc}>
                                {service.description}
                            </p>
                        </div>
                        <div className={styles.serviceImageContainer}>
                            {service.images && service.images.length > 0 ? (
                                <div style={{ width: '100%', height: '100%' }}>
                                    <Carousel 
                                        images={service.images} 
                                        autoPlay={true} 
                                        interval={5000} 
                                    />
                                </div>
                            ) : (
                                <div style={{ 
                                    width: '100%', 
                                    height: '300px', 
                                    backgroundColor: '#f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px'
                                }}>
                                    Aucune image disponible pour {service.title}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Services;
